/**
 * The colours and line weights of the drag affordances, in one place.
 *
 * Four visual layers answer four different questions, so each one gets its own
 * colour and they must not be confused:
 *
 * - **part** (azure) — *this is the thing you can move*. Worn by the part
 *   itself, via the fresnel glow in `DragHighlightGlow`.
 * - **guide** (white) — *where it may go*. Worn by the movement-area outline,
 *   the centre and diagonal guide lines, and the idle snap markers.
 * - **arrow** (azure, the part's own colour) — *which way it moves*. It shares
 *   the part's blue because the arrows belong to the part: they sit on it and
 *   move with it, and a second accent hue next to the glow read as a third
 *   thing on screen. What they must not share is the guides' colour: an arrow
 *   drawn along an edge part's travel line sits exactly on top of it, and in
 *   one colour the two merged into a single smear.
 * - **snap** (green) — *it is aligned right now*. Worn only by a guide line the
 *   part is snapped to and by the snap marker it landed on.
 *
 * ## Every stroke is a light/dark pair
 *
 * None of these colours may be trusted on its own, because the background is
 * chosen by the user and the set is open: the panel colours live in the options
 * database and are editable in the admin UI, and they already run from white
 * (RAL9003) through orange (RAL2008) and yellow (RAL1023) to black (RAL9004).
 * Amber on the orange laminate was the failure that prompted this — but every
 * fixed hue has some panel it disappears against, and tomorrow's colour could
 * be the one that kills whichever hue we picked today.
 *
 * So contrast comes from *structure*, not from hue. Guides are a white core
 * over a near-black casing, the cartographic convention: on a dark panel the
 * core carries, on a light panel the casing does, on a mid panel both do.
 *
 * Green and azure survive as accents *because* of the casing: they only ever
 * appear as the core of a cased stroke or on top of a cased marker. The arrows
 * are the one uncased accent: they are solid, opaque meshes with real depth
 * rather than strokes, and their dark hull kept breaking up along the shaft,
 * so they rely on the blue alone and on the glow next to them.
 */
/** Azure, a step lighter than dodger blue; the part and its arrows. */
const PART_BLUE = 0x3f96ff;

export const DRAG_COLORS = {
  /** The draggable part itself. */
  part: PART_BLUE,
  /**
   * The part glow's silhouette (pale sky). The rim lightens towards this so
   * the outline stays bright on a black grip or through a dark case wall,
   * where more of the base azure would only read as navy.
   */
  partRim: 0xbfe3ff,
  /** Core of the movement area, guide lines and idle snap markers. */
  guide: 0xffffff,
  /** Drawn under every guide stroke and behind every marker. */
  guideCasing: 0x0b0f14,
  /** The direction arrows, in the part's own colour. */
  arrow: PART_BLUE,
  /** A guide line or marker the part is currently snapped to. */
  snap: 0x00ff88,
  /** Debug-only outline, deliberately a colour nothing else uses. */
  debug: 0xff00ff,
} as const;

/**
 * How much wider the casing is than the core it protects, in screen pixels, and
 * how strongly it is drawn.
 *
 * Two pixels means one pixel of dark shows on each side of the core — enough to
 * separate the stroke from the panel, little enough that the stroke still reads
 * as its core colour rather than as a dark line with a light middle.
 */
export const CASING_EXTRA_PX = 2;
/** Opacity of the casing. Short of solid so it reads as a shadow, not a line. */
export const CASING_OPACITY = 0.6;

/**
 * Dash pattern of an idle guide line, in millimetres (the scene's local unit).
 *
 * The snap state is carried by dash *and* colour, not colour alone: a dashed
 * line going solid is legible against the brushed-aluminium profiles, where a
 * white-to-green change on a 2 px line is easy to miss, and it survives a
 * red-green colour deficiency.
 */
export const GUIDE_DASH_MM = 12;
/** Gap between dashes of an idle guide line, in millimetres. */
export const GUIDE_GAP_MM = 8;

/** The resolved look of one cased line, in screen-space pixels. */
export interface DragLineStyle {
  /** Colour of the core, the layer the user reads the state from. */
  color: number;
  opacity: number;
  /** Width in screen pixels — only honoured by `Line2`, see `CasedLine`. */
  lineWidth: number;
  /** Width of the dark layer drawn under the core. */
  casingWidth: number;
  casingOpacity: number;
}

/** The resolved look of a guide line, which can additionally be dashed. */
export interface GuideLineStyle extends DragLineStyle {
  dashed: boolean;
}

/** Wraps a core colour/width/opacity in the casing that makes it legible. */
function cased(color: number, opacity: number, lineWidth: number) {
  return {
    color,
    opacity,
    lineWidth,
    casingWidth: lineWidth + CASING_EXTRA_PX,
    casingOpacity: CASING_OPACITY,
  };
}

/**
 * The movement-area outline: the rectangle (or edge segment) the part may be
 * placed within.
 *
 * It keeps its idle colour while the drag runs and only gains weight and
 * opacity, because the area does not change when the part starts moving — what
 * changes is how much attention it deserves. Turning it green would claim a
 * snap that has not happened.
 */
export function outlineStyle(
  isDragging: boolean,
  debug: boolean,
): DragLineStyle {
  if (debug) return cased(DRAG_COLORS.debug, 0.8, 2.5);
  if (isDragging) return cased(DRAG_COLORS.guide, 1, 3);
  return cased(DRAG_COLORS.guide, 0.85, 2.5);
}

/**
 * A centre or diagonal guide line. Idle lines are held back — dashed, thin and
 * translucent — so a surface criss-crossed with them stays readable; the one
 * the part actually snaps to comes forward.
 */
export function guideStyle(snapped: boolean): GuideLineStyle {
  if (snapped) return { ...cased(DRAG_COLORS.snap, 1, 3), dashed: false };
  return { ...cased(DRAG_COLORS.guide, 0.6, 2), dashed: true };
}
